import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, FormControl } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import useIsCompany from '../../../myHooks/useIsCompany';
import { makeURL } from '../../../helperFunctions';
import useViewportWidth from '../../../myHooks/useViewportWidth';

const useStyles = makeStyles(theme => ({
  mainContainer: ({ isInGroup }) => ({
    width: '100%',
    maxWidth: '650px',
    margin: '0 auto 30px auto',
    gap: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: isInGroup ? '1px solid lightgray' : 'none',
    paddingBottom: isInGroup ? '25px' : 'none',
    flexWrap: 'wrap',

    '@media (max-width: 768px)': {
      width: '100% !important',
      maxWidth: '100%',
    },
  }),
  text: ({ isSummary }) => ({
    color: theme.palette.text.primary,
    fontWeight: 800,
    fontSize: '18px',
    margin: '0',
    textAlign: 'left',
    '@media (max-width: 600px)': {
      fontSize: '16px',
    },
  }),
  dropdownWrapper: {
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  dropdown: {
    padding: '10px 13px',
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  autocompleteDropdown: {
    paddingBottom: '0px',
  },
}));

const DropdownAutocomplete = props => {
  const { id } = useParams();
  const history = useHistory();
  const clientQuestionPresent = useIsCompany() && id === 'new';
  const appData = useSelector(({ data }) => data);

  const chosenClient = props.surveyAnswers?.['Social_Clients_Select'] || null;
  const clientBrandPersonas = appData.brandPersonas?.filter(e => e.agencyId === chosenClient) || [];

  useEffect(() => {
    if (!appData.brandPersonas?.length) {
      history.replace(makeURL('/brand-personas/all'));
      return;
    }

    if (clientQuestionPresent && (!chosenClient || !clientBrandPersonas.length)) {
      history.replace(makeURL('/clients/all'));
    }
  }, [appData.brandPersonas, clientQuestionPresent, chosenClient, clientBrandPersonas.length, history]);

  if (!appData.brandPersonas?.length || (clientQuestionPresent && (!chosenClient || !clientBrandPersonas.length))) {
    return null;
  }

  const autoCompleteData = (clientQuestionPresent ? clientBrandPersonas : appData.brandPersonas).map(persona => ({
    text: persona.Name,
    value: persona.id,
  }));

  const [selectedValue, setSelectedValue] = useState(autoCompleteData[0]?.value);

  useEffect(() => {
    if (!props.value) {
      props.handleSurveyAnswers(selectedValue, props.id);
    }
  }, []);

  const handleAutoCompleteChange = (event, personas) => {
    setSelectedValue(personas.value);
    props.handleSurveyAnswers(personas.value, props.id);
  };

  if (props.hasTextWrapper) {
    return (
      <AutocompleteWrapper text={props.text} isInGroup={props.isInGroup}>
        <AutocompleteInner
          autoCompleteData={autoCompleteData}
          onChange={handleAutoCompleteChange}
          selectedValue={selectedValue}
          value={props.value}
          isSummary={props.isSummary}
        />
      </AutocompleteWrapper>
    );
  } else {
    return (
      <AutocompleteInner
        autoCompleteData={autoCompleteData}
        onChange={handleAutoCompleteChange}
        selectedValue={selectedValue}
        value={props.value}
        isSummary={props.isSummary}
      />
    );
  }
};

const AutocompleteWrapper = props => {
  const classes = useStyles({ isInGroup: props.isInGroup });

  return (
    <Box className={classes.mainContainer}>
      <Typography component="div" variant="h3" className={classes.text}>
        {props.text}
      </Typography>
      {props.children}
    </Box>
  );
};

const AutocompleteInner = props => {
  const classes = useStyles({ isSummary: props.isSummary });

  let width = useViewportWidth();
  const isBigScreen = width > 400;

  return (
    <Box className={classes.dropdownWrapper} style={{ minWidth: props.isSummary && isBigScreen ? '360px' : '300px' }}>
      <FormControl fullWidth>
        <Autocomplete
          options={props.autoCompleteData}
          getOptionLabel={persona => persona.text}
          renderInput={params => (
            <TextField {...params} variant="outlined" size="small" className={classes.autocompleteDropdown} />
          )}
          getOptionSelected={(option, personas) => option.value === personas.value}
          onChange={props.onChange}
          disableClearable={true}
          value={
            props.value
              ? props.autoCompleteData.find(element => element.value === props.value)
              : props.autoCompleteData.find(element => element.value === props.selectedValue)
          }
        />
      </FormControl>
    </Box>
  );
};

export default DropdownAutocomplete;
